/**
 * The hologram layout (D169): {@link MapGraph} → an isometric drawing of
 * rooms as volumes on stacked floors, ready for any SVG serializer.
 *
 * @remarks
 * The same discipline as `map-svg-layout.ts`: the geometry lives HERE,
 * once, and a serializer only maps primitives onto elements. Every room is
 * a box — its footprint and height by the pack's `kind` (a corridor long
 * and low along its links, a chamber wide and tall, a shaft thin and
 * tall) — projected 2:1 dimetric, with the three faces a viewer sees as
 * three polygons so a stylesheet can light the top and shade the sides.
 * Floors rise on screen with the level; a lift is the vertical between two
 * boxes on the same cell. Corridors are drawn on the floor plane, under the
 * boxes, so what shows is the stretch between them.
 *
 * Painter's order is the primitives' order: floors first, corridors, then
 * nodes lowest floor first and back to front within a floor, then lifts
 * over everything — a serializer that draws in sequence draws correctly.
 * Colors are not decided here: the material is one CSS custom property the
 * pack may tint (`--orpheus-holo`), and every primitive carries its
 * semantic flags for the stylesheet to read.
 *
 * @packageDocumentation
 */

import type { MapRoomKind } from '@orpheus/content-schema';

import type { MapGraph, MapGraphNode, MapNodeKnowledge } from './map-graph.js';

import { UNKNOWN_LABEL } from './derive-map-graph.js';
import { floorLabelOf, footprintOf, legendOf, type MapLegendKey } from './map-footprints.js';
import {
	boundsOf,
	floorLabelRectOf,
	type LabelAnchor,
	labelRectOf,
	type LabelSize,
	type LabelSubject,
	type LabelText,
	type PlacedLabel,
	placeLabels,
	type Point,
	type Quad,
	type Rect,
} from './map-holo-labels.js';
import { describeGraph } from './map-svg-layout.js';

/** Half a cell on screen, across and down — the 2:1 dimetric projection. */
const HALF_WIDTH = 60;
const HALF_HEIGHT = 30;
/** How far up the screen each floor sits above the one below. */
const FLOOR_RISE = 118;
const CANVAS_PADDING = 28;
/** How far a floor plate reaches past the outermost cell on it, in cells. */
const FLOOR_MARGIN = 0.62;
/** Half a corridor's width, in cells. */
const CORRIDOR_HALF_WIDTH = 0.09;

/** How tall each kind of room is drawn, on screen; the footprint is the styles' shared table (`map-footprints.ts`). */
const HEIGHTS: Readonly<Record<MapRoomKind, number>> = {
	chamber: 32,
	corridor: 10,
	room: 20,
	shaft: 46,
};

/** One floor plate: the diamond under every box on a level, and its name. */
export interface MapHoloFloorPrimitive {
	readonly label: string;
	readonly labelX: number;
	readonly labelY: number;
	readonly level: number;
	/** The plate's four corners, as an SVG `points` string. */
	readonly points: string;
}

/** A room's name as placed (D174): where it hangs, how, at what size, and the leader when it floats. */
export interface MapHoloNodeLabel {
	/** How the text hangs from `x`: centered on the face or above it, or beside the box at one end. */
	readonly anchor: LabelAnchor;
	/** From the box's top apex up to a label that floats above it; absent when the label is on or beside the box. */
	readonly leader?: MapHoloLiftPrimitive;
	/** The hologram's own size, or a size down — a walked room the player is not in, on a narrow figure (D224). */
	readonly size: LabelSize;
	readonly text: string;
	readonly x: number;
	readonly y: number;
}

/** One room as a box: three visible faces, a label placed clear of everything else (D174), and the node's flags. */
export interface MapHoloNodePrimitive {
	readonly isActive: boolean;
	readonly isCheckpoint: boolean;
	readonly key: string;
	readonly kind: MapRoomKind;
	readonly knowledge: MapNodeKnowledge;
	/** The room's name as placed; absent for an unexplored room's stub on a narrow figure, which is drawn and not named (D224). */
	readonly label?: MapHoloNodeLabel;
	/** The face toward the viewer's left, as an SVG `points` string. */
	readonly left: string;
	readonly level: number;
	/** The face toward the viewer's right. */
	readonly right: string;
	/** The top face. */
	readonly top: string;
}

/** One corridor on the floor plane between two cells. */
export interface MapHoloCorridorPrimitive {
	readonly level: number;
	readonly points: string;
}

/** One lift: the vertical between a cell on one floor and the same cell on the next. */
export interface MapHoloLiftPrimitive {
	readonly x1: number;
	readonly x2: number;
	readonly y1: number;
	readonly y2: number;
}

/** What the legend has to explain, given what the drawing contains — the table every volumetric style shares. */
export type MapHoloLegendKey = MapLegendKey;

/**
 * What the drawing's host knows that the graph does not (D224).
 *
 * @remarks
 * `isNarrow`: the figure is drawn at a phone's width — below the shell's
 * rail breakpoint. There the hologram follows the scene's answer to crowding
 * (Mike, 2026-10-01, via Calder's `the-phone-names` §1 and §3): an
 * unexplored room's stub is drawn and not named, so it lifts nothing; and
 * every walked room but the one the player stands in is named a size down,
 * the narrower advance fed into the settle so the leaders reflect it. Wide,
 * the layout is what D174 made it, byte for byte.
 */
export interface MapHoloLayoutOptions {
	readonly isNarrow?: boolean;
}

/** A complete positioned hologram, ready for any SVG serializer. */
export interface MapHoloLayout {
	readonly corridors: readonly MapHoloCorridorPrimitive[];
	/** Long-form description for `<desc>` / `aria-describedby`. */
	readonly description: string;
	readonly floors: readonly MapHoloFloorPrimitive[];
	readonly height: number;
	readonly legend: readonly MapHoloLegendKey[];
	readonly lifts: readonly MapHoloLiftPrimitive[];
	readonly nodes: readonly MapHoloNodePrimitive[];
	readonly width: number;
}

/** A quad with every corner moved. */
const mapQuad = (quad: Quad, move: (point: Point) => Point): Quad => [move(quad[0]), move(quad[1]), move(quad[2]), move(quad[3])];

/** Project a cell position (fractional cells allowed) on a floor, at a height above it. */
const project = (x: number, y: number, level: number, height = 0): Point => [(x - y) * HALF_WIDTH, (x + y) * HALF_HEIGHT - level * FLOOR_RISE - height];

const round = (value: number): number => Math.round(value * 10) / 10;

/** An SVG `points` string. */
const pointsOf = (points: readonly Point[]): string => points.map(([x, y]) => `${String(round(x))},${String(round(y))}`).join(' ');

/** The half-extents of a node's box in grid units and its height on screen. */
const extentsOf = (graph: MapGraph, node: MapGraphNode): { readonly halfX: number; readonly halfY: number; readonly height: number } => ({
	...footprintOf(graph, node),
	height: HEIGHTS[node.kind],
});

/** The painter's order for nodes: lowest floor first, then back to front. */
const paintOrder = (left: MapGraphNode, right: MapGraphNode): number => left.level - right.level || left.x + left.y - (right.x + right.y) || left.x - right.x;

/** The order labels are placed in: the active room first, then front to back — the names read first get the best ground. */
const labelOrder = (left: MapGraphNode, right: MapGraphNode): number =>
	Number(right.isActive) - Number(left.isActive) || right.x + right.y - (left.x + left.y) || right.level - left.level || left.x - right.x;

/** The four corners of a rectangle, for fitting the canvas around it. */
const cornersOf = (rect: Rect): readonly Point[] => [
	[rect.left, rect.top],
	[rect.right, rect.bottom],
];

/** Every point a drawing spans, so the canvas can be fitted around it — the labels included, since they may float past the boxes. */
const collectPoints = (parts: {
	readonly floors: readonly Quad[];
	readonly labels: readonly Rect[];
	readonly lifts: readonly (readonly [Point, Point])[];
	readonly nodes: readonly Quad[];
}): readonly Point[] => [...parts.floors.flat(), ...parts.nodes.flat(), ...parts.lifts.flat(), ...parts.labels.flatMap(cornersOf)];

/** A placed label on screen, fitted to the canvas, with its leader when it floats. */
const fittedLabel = (subject: SizedLabelText, placement: PlacedLabel, fit: (point: Point) => Point): MapHoloNodeLabel => {
	const [x, y] = fit([placement.x, placement.y]);

	return {
		anchor: placement.anchor,
		...(placement.leader === undefined ? {} : { leader: fittedLine(placement.leader, fit) }),
		size: subject.size,
		text: subject.label,
		x: round(x),
		y: round(y),
	};
};

/** A line on screen, fitted to the canvas. */
const fittedLine = (line: MapHoloLiftPrimitive, fit: (point: Point) => Point): MapHoloLiftPrimitive => {
	const [x1, y1] = fit([line.x1, line.y1]);
	const [x2, y2] = fit([line.x2, line.y2]);

	return { x1: round(x1), x2: round(x2), y1: round(y1), y2: round(y2) };
};

/**
 * The label of a node, with the active room's marker — the type it is set
 * in, which is the estimate's to know (D176), and the size: the hologram's
 * own, or a size down for a walked room the player is not in on a narrow
 * figure (D224).
 */
const nodeLabelOf = (node: MapGraphNode, isNarrow: boolean): SizedLabelText => {
	const label = node.name ?? UNKNOWN_LABEL;

	return {
		label: node.isActive ? `▸ ${label}` : label,
		size: isNarrow && !node.isActive ? 'small' : 'base',
		type: node.knowledge === 'unknown' ? 'unknown' : 'name',
	};
};

/** A label's text with its size decided — every room's name on the hologram has one. */
type SizedLabelText = { readonly size: LabelSize } & LabelText;

/** Whether a node is named on the drawing: every node on a wide figure; on a narrow one, every node but an unexplored room's stub (D224). */
const isNamed = (node: MapGraphNode, isNarrow: boolean): boolean => !isNarrow || node.knowledge !== 'unknown';

/**
 * Position one derived map as a hologram.
 *
 * @param graph - The derived map, derived with levels
 * @param options - What the host knows: whether the figure is narrow
 * @returns The positioned primitives
 */
export const buildMapHoloLayout = (graph: MapGraph, options: MapHoloLayoutOptions = {}): MapHoloLayout => {
	const isNarrow = options.isNarrow === true;
	const nodes = [...graph.nodes].sort(paintOrder);
	const levels = [...new Set(nodes.map((node) => node.level))].sort((left, right) => left - right);

	// Floors: one plate per level, around the cells on it.
	const floorCorners = levels.map((level) => {
		const onLevel = nodes.filter((node) => node.level === level);
		const minX = Math.min(...onLevel.map((node) => node.x)) - FLOOR_MARGIN;
		const maxX = Math.max(...onLevel.map((node) => node.x)) + FLOOR_MARGIN;
		const minY = Math.min(...onLevel.map((node) => node.y)) - FLOOR_MARGIN;
		const maxY = Math.max(...onLevel.map((node) => node.y)) + FLOOR_MARGIN;

		const corners: Quad = [project(minX, minY, level), project(maxX, minY, level), project(maxX, maxY, level), project(minX, maxY, level)];

		return { corners, level };
	});

	// Nodes: the three visible faces of each box.
	const boxes = nodes.map((node) => {
		const { halfX, halfY, height } = extentsOf(graph, node);
		const corners: Quad = [
			[node.x - halfX, node.y - halfY],
			[node.x + halfX, node.y - halfY],
			[node.x + halfX, node.y + halfY],
			[node.x - halfX, node.y + halfY],
		];
		const base = mapQuad(corners, ([x, y]) => project(x, y, node.level));
		const top = mapQuad(corners, ([x, y]) => project(x, y, node.level, height));

		return { base, node, top };
	});

	const liftEnds = graph.lifts.map((lift) => [project(lift.x, lift.y, lift.fromLevel), project(lift.x, lift.y, lift.toLevel)] as const);

	// Labels (D174): placed clear of every box, floor label and each other,
	// the active room first and the nearest boxes next; unfitted, like all
	// the geometry above, so the canvas can be fitted around them too. D224:
	// on a narrow figure a stub is not a subject — it gets no name and lifts
	// nothing — but its box is still ground nobody's name may cover, so its
	// silhouette joins the reserved rectangles.
	const floorLabelRects = floorCorners.map(({ corners, level }) => floorLabelRectOf(floorLabelOf(level), corners[3][0] + 6, corners[3][1] - 6));
	const named = boxes.filter((box) => isNamed(box.node, isNarrow));
	const unnamed = boxes.filter((box) => !isNamed(box.node, isNarrow));
	const labeled = placeLabels(
		[...named]
			.sort((left, right) => labelOrder(left.node, right.node))
			.map((box): { readonly box: typeof box } & LabelSubject & SizedLabelText => ({ ...nodeLabelOf(box.node, isNarrow), base: box.base, box, top: box.top })),
		[...floorLabelRects, ...unnamed.map((box) => boundsOf([...box.base, ...box.top]))],
	);
	const placementsByBox = new Map(labeled.map(([subject, placement]) => [subject.box, { placement, subject }]));
	const spanned = collectPoints({
		floors: floorCorners.map((floor) => floor.corners),
		labels: labeled.map(([subject, placement]) => labelRectOf(subject, placement.x, placement.y, placement.anchor)),
		lifts: liftEnds,
		nodes: boxes.flatMap((box) => [box.base, box.top]),
	});
	const minX = Math.min(...spanned.map(([x]) => x));
	const minY = Math.min(...spanned.map(([, y]) => y));
	const maxX = Math.max(...spanned.map(([x]) => x));
	const maxY = Math.max(...spanned.map(([, y]) => y));
	// Fit the canvas: every point shifted so the drawing starts at the padding.
	const fit = ([x, y]: Point): Point => [x - minX + CANVAS_PADDING, y - minY + CANVAS_PADDING];

	return {
		corridors: graph.edges.map((edge) => {
			const dx = edge.toX - edge.fromX;
			const dy = edge.toY - edge.fromY;
			const length = Math.hypot(dx, dy);
			const nx = (-dy / length) * CORRIDOR_HALF_WIDTH;
			const ny = (dx / length) * CORRIDOR_HALF_WIDTH;

			return {
				level: edge.level,
				points: pointsOf(
					[
						project(edge.fromX + nx, edge.fromY + ny, edge.level),
						project(edge.toX + nx, edge.toY + ny, edge.level),
						project(edge.toX - nx, edge.toY - ny, edge.level),
						project(edge.fromX - nx, edge.fromY - ny, edge.level),
					].map(fit),
				),
			};
		}),
		description: `${describeGraph(graph)}${levels.length > 1 ? ` Across ${String(levels.length)} floors.` : ''}`,
		floors: floorCorners.map(({ corners, level }) => {
			const fitted = mapQuad(corners, fit);
			const leftCorner = fitted[3];

			return { label: floorLabelOf(level), labelX: round(leftCorner[0] + 6), labelY: round(leftCorner[1] - 6), level, points: pointsOf(fitted) };
		}),
		height: round(maxY - minY + CANVAS_PADDING * 2),
		legend: legendOf(graph),
		lifts: liftEnds.map(([from, to]) => {
			const [x1, y1] = fit(from);
			const [x2, y2] = fit(to);

			return { x1: round(x1), x2: round(x2), y1: round(y1), y2: round(y2) };
		}),
		// The boxes are already in painter's order; each takes the label placed for it, if one was.
		nodes: boxes.map((box) => {
			const { base, node, top } = box;
			const [topA, topB, topC, topD] = mapQuad(top, fit);
			const [, baseB, baseC, baseD] = mapQuad(base, fit);
			const placed = placementsByBox.get(box);

			return {
				isActive: node.isActive,
				isCheckpoint: node.isCheckpoint,
				key: `${String(node.x)},${String(node.y)},${String(node.level)}`,
				kind: node.kind,
				knowledge: node.knowledge,
				...(placed === undefined ? {} : { label: fittedLabel(placed.subject, placed.placement, fit) }),
				left: pointsOf([topC, topD, baseD, baseC]),
				level: node.level,
				right: pointsOf([topB, topC, baseC, baseB]),
				top: pointsOf([topA, topB, topC, topD]),
			};
		}),
		width: round(maxX - minX + CANVAS_PADDING * 2),
	};
};
